const HEIC_MIME_TYPES = new Set([
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
]);

const HEIC_EXTENSION_PATTERN = /\.(heic|heif)$/i;

const WEBP_MIME_TYPES = new Set(['image/webp']);
const WEBP_EXTENSION_PATTERN = /\.webp$/i;

const HEIC_FILE_BRANDS = new Set([
  'heic',
  'heif',
  'heix',
  'hevc',
  'hevx',
  'heim',
  'heis',
  'hevm',
  'hevs',
]);

const FILE_TYPE_BOX = 'ftyp';
const FILE_TYPE_BOX_HEADER_SIZE = 16;
const FILE_BRAND_SIZE = 4;
const FILE_SIGNATURE_READ_SIZE = 128;
const WEBP_SIGNATURE_READ_SIZE = 12;

export const HEIC_ACCEPT_TYPES = [
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
  '.heic',
  '.heif',
];

export const WEBP_ACCEPT_TYPES = ['image/webp', '.webp'];

export const isHeicImage = (file: Pick<File, 'name' | 'type'>) =>
  HEIC_MIME_TYPES.has(file.type.toLowerCase()) ||
  HEIC_EXTENSION_PATTERN.test(file.name);

export const isWebpImage = (file: Pick<File, 'name' | 'type'>) =>
  WEBP_MIME_TYPES.has(file.type.toLowerCase()) ||
  WEBP_EXTENSION_PATTERN.test(file.name);

const readBlobAsArrayBuffer = (blob: Blob): Promise<ArrayBuffer> => {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
};

const readAscii = (bytes: Uint8Array, offset: number) =>
  String.fromCharCode(...bytes.slice(offset, offset + FILE_BRAND_SIZE));

export const hasHeicFileSignature = async (file: Blob) => {
  if (file.size < FILE_TYPE_BOX_HEADER_SIZE) {
    return false;
  }

  const bytes = new Uint8Array(
    await readBlobAsArrayBuffer(file.slice(0, FILE_SIGNATURE_READ_SIZE))
  );
  if (readAscii(bytes, 4) !== FILE_TYPE_BOX) {
    return false;
  }

  const boxSize = new DataView(
    bytes.buffer,
    bytes.byteOffset,
    bytes.byteLength
  ).getUint32(0);
  if (boxSize !== 0 && boxSize < FILE_TYPE_BOX_HEADER_SIZE) {
    return false;
  }

  const brandEnd =
    boxSize === 0 ? bytes.length : Math.min(boxSize, bytes.length);
  const brands = [readAscii(bytes, 8)];
  for (
    let offset = FILE_TYPE_BOX_HEADER_SIZE;
    offset + 3 < brandEnd;
    offset += 4
  ) {
    brands.push(readAscii(bytes, offset));
  }

  return brands.some((brand) => HEIC_FILE_BRANDS.has(brand.toLowerCase()));
};

export const hasWebpFileSignature = async (file: Blob) => {
  if (file.size < WEBP_SIGNATURE_READ_SIZE) {
    return false;
  }

  const bytes = new Uint8Array(
    await readBlobAsArrayBuffer(file.slice(0, WEBP_SIGNATURE_READ_SIZE))
  );

  return readAscii(bytes, 0) === 'RIFF' && readAscii(bytes, 8) === 'WEBP';
};

export const isHeicImageFile = async (file: File) => {
  if (isHeicImage(file)) {
    return true;
  }
  if (file.type.toLowerCase().startsWith('image/')) {
    return false;
  }
  return hasHeicFileSignature(file);
};

export const isWebpImageFile = async (file: File) => {
  if (isWebpImage(file)) {
    return true;
  }
  if (file.type.toLowerCase().startsWith('image/')) {
    return false;
  }
  return hasWebpFileSignature(file);
};

export const isSupportedImageFile = async (file: File) =>
  file.type.toLowerCase().startsWith('image/') ||
  (await isHeicImageFile(file)) ||
  isWebpImageFile(file);

const makeJpegFilename = (filename: string) => {
  const basename = filename
    .replace(HEIC_EXTENSION_PATTERN, '')
    .replace(WEBP_EXTENSION_PATTERN, '');
  return `${basename || 'image'}.jpg`;
};

const canvasToJpegBlob = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Image conversion did not return a JPEG.'));
        }
      },
      'image/jpeg',
      0.9
    );
  });

const convertWebpToJpeg = async (file: File): Promise<File> => {
  const source = WEBP_MIME_TYPES.has(file.type.toLowerCase())
    ? file
    : new Blob([file], { type: 'image/webp' });
  const objectUrl = URL.createObjectURL(source);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () =>
        reject(new Error('WebP image could not be read.'));
      element.src = objectUrl;
    });

    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;
    if (!width || !height) {
      throw new Error('WebP image has invalid dimensions.');
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas is unavailable for image conversion.');
    }

    context.fillStyle = '#fff';
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    const jpegBlob = await canvasToJpegBlob(canvas);
    return new File([jpegBlob], makeJpegFilename(file.name), {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

export const normalizeImageForUpload = async (file: File): Promise<File> => {
  if (await isWebpImageFile(file)) {
    return convertWebpToJpeg(file);
  }

  if (!(await isHeicImageFile(file))) {
    return file;
  }

  const { default: heic2any } = await import('heic2any');
  const result = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.9,
  });
  const jpegBlob = Array.isArray(result) ? result[0] : result;

  if (!(jpegBlob instanceof Blob)) {
    throw new Error('HEIC conversion did not return an image.');
  }

  return new File([jpegBlob], makeJpegFilename(file.name), {
    type: 'image/jpeg',
    lastModified: file.lastModified,
  });
};
